import ChooseAdvancingTemplate from "./ChooseAdvancingTemplate";
import React from "react";
import Modal from "components/modal";
import { useMutation } from "@tanstack/react-query";
import { Button } from "semantic-ui-react";
import SelectContact from "components/AddressBook/SelectContact";
import EditParticipantAsContact from "eSign/components/modals/EditParticipantAsContact";
import { type SetParticipantFromContactParams } from "eSign/components/Participant/hooks";

interface Props {
  showId: number
  templates: server.dto.AdvancingTemplate[]
  onSessionCreated: () => void
  onClose: () => void
}

const StartAdvancingSessionModal = (props: Props) => {
  const hasSingleTemplate = props.templates.length === 1;

  const [step, setStep] = React.useState<"chooseTemplate" | "selectContact" | "contactDetails">(hasSingleTemplate ? "selectContact" : "chooseTemplate");
  const [selectedTemplate, setSelectedTemplate] = React.useState<server.dto.AdvancingTemplate>(hasSingleTemplate ? props.templates[0] : undefined);
  const [contact, setContact] = React.useState<{
    contactId: number
    contactName: string
    culture: server.dto.Culture
    email: string
  }>(undefined);

  const createSessionMutation = useMutation({
    mutationFn: async ({ email, contactId, culture, invitationBody, invitationSubject }: SetParticipantFromContactParams) => {
      const res = await window.CoreApi.AdvancingSession.Create({
        showId: props.showId,
        advancingTemplateId: selectedTemplate.advancingTemplateId,
        participants: [{
          contactId,
          contactCulture: culture,
          email,
          invitationSubject,
          invitationBody
        }]
      });

      if (res.data.hasErrors) {
        return res.data.errors[0].message;
      }

      props.onSessionCreated();
    }
  });

  const onTemplateSelected = (template: server.dto.AdvancingTemplate) => {
    setSelectedTemplate(template);
    setStep("selectContact");
  };

  const onContactSelected = (contact: server.dto.ContactAddressBookEntry, culture: server.dto.Culture, email: string) => {
    setContact({
      contactId: contact.contactId,
      contactName: contact.name.fullName,
      culture,
      email
    });
    setStep("contactDetails");
  };

  if (step === "chooseTemplate") {
    return <ChooseAdvancingTemplate
      templates={props.templates}
      onTemplateSelected={onTemplateSelected}
      onClose={props.onClose}
    />;
  }

  if (step === "selectContact") {
    return <Modal
      isOpen
      handleClose={props.onClose}
      title="Start new advancing session"
      width="small"
      height={750}
    >
      <SelectContact
        hasNoCompany
        suggestedApiInstruction={{ itemId: props.showId, mainArea: "show" }}
        onContactSelected={onContactSelected}
      />

      {!hasSingleTemplate && <div className="flex">
        <Button color="blue" onClick={() => { setStep("chooseTemplate"); }} type="button">
          {RESX.GeneralLabels.Previous}
        </Button>
      </div>}
    </Modal>;
  }

  return <Modal
    isOpen
    handleClose={props.onClose}
    title="Start new advancing session"
    width="small"
  >
    <EditParticipantAsContact
      contactName={contact.contactName}
      contactId={contact.contactId}
      initialEmail={contact.email}
      initialCompany={null}
      initialCulture={contact.culture}
      initialInvitationBody=""
      initialInvitationSubject=""
      saveMutation={createSessionMutation}
      customLabel={RESX.Buttons.Invite}
      isViewer
      onPreviousStepClicked={() => {
        setContact(undefined);
        setStep("selectContact");
      }}
      messageTemplateType="invitationForAdvancing"
    />
  </Modal>;
};

export default StartAdvancingSessionModal;
